import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
/* ── Types ── */
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
}
/* ── Static data ── */
const NAMES = ['María', 'Carlos', 'Ana', 'Roberto', 'Lucía', 'Fernando', 'Graciela', 'Diego', 'Valentina', 'Martín', 'Claudia', 'Alejandro', 'Sofía', 'Gustavo', 'Patricia', 'Andrés', 'Laura', 'Jorge', 'Camila', 'Ricardo', 'Florencia', 'Pablo', 'Daniela', 'Sebastián', 'Carolina', 'Nicolás', 'Gabriela', 'Ignacio', 'Natalia', 'Matías', 'Verónica', 'Santiago', 'Cecilia', 'Emilio', 'Andrea', 'Rodrigo', 'Mariana', 'Tomás', 'Lorena', 'Federico'];
const CITIES = ['Montevideo', 'Salto', 'Punta del Este', 'Colonia', 'Rivera', 'Maldonado', 'Paysandú', 'Las Piedras', 'Melo', 'Tacuarembó', 'Durazno', 'Treinta y Tres', 'Rocha', 'Florida', 'San José', 'Canelones', 'Mercedes', 'Minas', 'Fray Bentos', 'Artigas', 'Carmelo', 'Atlántida', 'Chuy', 'La Paloma'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'El Gran Recetario Ancestral',
  editionLabel: 'Pack Completo',
  priceNumber: 490,
  priceText: '$490 UYU',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/8qwq7iJH71X98zmPKkuWDH/WhatsApp_Image_2026-03-16_at_20.07.50.jpg'
}, {
  title: 'Protocolo de 7 Días',
  editionLabel: 'El Reinicio Maestro',
  priceNumber: 290,
  priceText: '$290 UYU',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/iSQWY87umZirKrkag3Gn1A/Protocolo_de_7_dias.png'
}, {
  title: 'Guía de Seguridad',
  editionLabel: 'Blindaje Ancestral',
  priceNumber: 290,
  priceText: '$290 UYU',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/pz6PPL92XTCjouZ8thSjin/Guia_de_Seguridad_Blindaje_Ancestral.png'
}];
/* ── Product scanner ── */
function scanProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // 1) Try JSON-LD
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description?.slice(0, 30) || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${offer.price} ${offer.priceCurrency || ''}`.trim(),
            thumbnailUrl: (Array.isArray(data.image) ? data.image[0] : data.image) || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // 2) Scan price-like elements
    const priceRegex = /\$\s?(\d{1,3}(?:[.,]\d{3})*(?:\.\d{2})?)/;
    const allElements = document.querySelectorAll('[class*="price"], [class*="Price"], [data-price]');
    allElements.forEach(el => {
      const match = el.textContent?.match(priceRegex);
      if (match) {
        const parent = el.closest('section, article, div[class*="card"], div[class*="product"]');
        const img = parent?.querySelector('img');
        const heading = parent?.querySelector('h1, h2, h3, h4, p[class*="title"], p[class*="name"]');
        products.push({
          title: heading?.textContent?.trim().slice(0, 60) || 'Producto',
          editionLabel: '',
          priceNumber: parseFloat(match[1].replace(',', '')) || 0,
          priceText: match[0],
          thumbnailUrl: img?.getAttribute('src') || ''
        });
      }
    });
    if (products.length > 0) return products;
  } catch {}
  return [];
}
/* ── Pick random without recent repeats ── */
function pickRandom<T>(list: T[], recentHistory: T[], historySize: number): T {
  const available = list.filter(item => !recentHistory.includes(item));
  const pool = available.length > 0 ? available : list;
  return pool[Math.floor(Math.random() * pool.length)];
}
/* ── Component ── */
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const catalogRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const showNotification = useCallback(() => {
    const catalog = catalogRef.current;
    const product = catalog[Math.floor(Math.random() * catalog.length)];
    const name = pickRandom(NAMES, nameHistoryRef.current, 10);
    nameHistoryRef.current = [...nameHistoryRef.current.slice(-9), name];
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    counterRef.current += 1;
    setNotification({
      id: counterRef.current,
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4s
    setTimeout(() => setVisible(false), 4000);
  }, []);
  useEffect(() => {
    // Scan on mount
    const scanned = scanProducts();
    catalogRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
    // First notification after 3s
    const firstTimer = setTimeout(showNotification, 3000);
    // Then repeat at random 8-15s intervals
    let intervalTimer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const delay = 8000 + Math.random() * 7000;
      intervalTimer = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, delay);
    };
    const startRepeat = setTimeout(() => scheduleNext(), 3000 + 4500);
    return () => {
      clearTimeout(firstTimer);
      clearTimeout(startRepeat);
      clearTimeout(intervalTimer);
    };
  }, [showNotification]);
  const motionProps = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.15
    }
  } : {
    initial: {
      opacity: 0,
      y: 60
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 60
    },
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" data-id="element-50">
      <AnimatePresence mode="wait" data-id="element-51">
        {visible && notification && <motion.div key={notification.id} {...motionProps} className="pointer-events-auto bg-white rounded-xl shadow-xl border border-gray-200 px-3 py-2.5 flex items-center gap-3 max-w-[360px] w-max" data-id="element-52">
            {/* Thumbnail */}
            {notification.product.thumbnailUrl && <img src={notification.product.thumbnailUrl} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0 shadow-sm" data-id="element-53" />}

            {/* Text */}
            <div className="min-w-0" data-id="element-54">
              <p className="text-xs text-gray-800 leading-snug" data-id="element-55">
                <span className="font-bold" data-id="element-56">{notification.name}</span> en{' '}
                <span className="font-semibold text-gray-600" data-id="element-57">
                  {notification.city}
                </span>{' '}
                compró
              </p>
              <p className="text-xs font-bold text-emerald-700 leading-snug truncate" data-id="element-58">
                {notification.product.title}
                {notification.product.editionLabel && <span className="font-normal text-gray-500" data-id="element-59">
                    {' '}
                    — {notification.product.editionLabel}
                  </span>}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-60">
                {notification.product.priceText} · hace{' '}
                {Math.floor(Math.random() * 8) + 1} min
              </p>
            </div>

            {/* Green dot */}
            <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0 animate-pulse" data-id="element-61" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}